import React, { createContext, useCallback, useContext, useMemo, useState } from "react";

/**
 * DSGVO-konformes Cookie-Consent-Management.
 *
 * Speichert die Einwilligung revisionssicher im localStorage inkl.
 * Zeitstempel und Versionierung. Falls die Version im Code angehoben
 * wird (z. B. weil sich Kategorien ändern), wird der alte Consent
 * verworfen und der Nutzer wird erneut gefragt.
 */

const STORAGE_KEY = "hh_cookie_consent";
const CONSENT_VERSION = 1;

export const COOKIE_CATEGORIES = [
  {
    id: "necessary",
    label: "Notwendige Cookies",
    description:
      "Diese Cookies sind für den technischen Betrieb der Website zwingend erforderlich, z. B. um sicherzustellen, dass Formular-Anfragen ankommen. Sie können nicht deaktiviert werden.",
    required: true,
  },
  {
    id: "statistics",
    label: "Statistik & Analyse",
    description:
      "Anonyme Reichweitenmessung, damit wir verstehen, welche Inhalte funktionieren. Es werden keine personenbezogenen Profile gebildet.",
    required: false,
  },
  {
    id: "marketing",
    label: "Marketing",
    description:
      "Erlaubt uns, den Erfolg einzelner Kampagnen (z. B. Instagram-Ads) zu messen und dir relevante Werbung außerhalb dieser Website auszuspielen.",
    required: false,
  },
  {
    id: "external",
    label: "Externe Medien",
    description:
      "Einbettung von Inhalten Dritter, z. B. YouTube-Videos, Google Maps oder Social-Media-Feeds. Diese Anbieter können eigene Cookies setzen.",
    required: false,
  },
];

const defaultChoices = () =>
  COOKIE_CATEGORIES.reduce((acc, c) => ({ ...acc, [c.id]: c.required }), {});

function readStoredConsent() {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || parsed.v !== CONSENT_VERSION) return null;
    // Ensure all keys are present (forward compatibility)
    const merged = { ...defaultChoices(), ...(parsed.categories || {}) };
    return { ...parsed, categories: merged };
  } catch {
    return null;
  }
}

function writeStoredConsent(categories) {
  if (typeof window === "undefined") return null;
  const payload = {
    v: CONSENT_VERSION,
    ts: new Date().toISOString(),
    categories: { ...categories, necessary: true },
  };
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // Storage nicht verfügbar (Safari Private Mode etc.) – Consent bleibt session-only
  }
  return payload;
}

const CookieConsentContext = createContext(null);

export function CookieConsentProvider({ children }) {
  const initial = readStoredConsent();
  const [consent, setConsent] = useState(initial);
  const [isBannerOpen, setBannerOpen] = useState(!initial);
  const [openInDetailMode, setOpenInDetailMode] = useState(false);

  const acceptAll = useCallback(() => {
    const all = COOKIE_CATEGORIES.reduce((acc, c) => ({ ...acc, [c.id]: true }), {});
    const payload = writeStoredConsent(all);
    setConsent(payload);
    setBannerOpen(false);
  }, []);

  const rejectAll = useCallback(() => {
    const only = defaultChoices();
    const payload = writeStoredConsent(only);
    setConsent(payload);
    setBannerOpen(false);
  }, []);

  const saveSelection = useCallback((selection) => {
    const payload = writeStoredConsent(selection);
    setConsent(payload);
    setBannerOpen(false);
  }, []);

  const openSettings = useCallback(() => {
    setOpenInDetailMode(true);
    setBannerOpen(true);
  }, []);

  const resetConsent = useCallback(() => {
    if (typeof window !== "undefined") window.localStorage.removeItem(STORAGE_KEY);
    setConsent(null);
    setOpenInDetailMode(false);
    setBannerOpen(true);
  }, []);

  const value = useMemo(
    () => ({
      consent,
      hasChoice: !!consent,
      isBannerOpen,
      openInDetailMode,
      setOpenInDetailMode,
      acceptAll,
      rejectAll,
      saveSelection,
      openSettings,
      resetConsent,
    }),
    [consent, isBannerOpen, openInDetailMode, acceptAll, rejectAll, saveSelection, openSettings, resetConsent]
  );

  return <CookieConsentContext.Provider value={value}>{children}</CookieConsentContext.Provider>;
}

export function useCookieConsent() {
  const ctx = useContext(CookieConsentContext);
  if (!ctx) throw new Error("useCookieConsent must be used inside <CookieConsentProvider>");
  return ctx;
}
